import React, { useState, useEffect } from 'react';
import api from './api';
import PioneerBadge from './PioneerBadge';
import useIsMobile from './hooks/useIsMobile';

const ReferralDashboard = ({ onBack, user }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await api.get('/pioneers/dashboard');
        setDashboard(res.data);
      } catch (err) {
        console.error('Error loading dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const handleCopyLink = () => {
    if (dashboard?.referral_url) {
      navigator.clipboard.writeText(dashboard.referral_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGenerateCode = async () => {
    try {
      const res = await api.post('/pioneers/generate-code');
      setDashboard(d => ({ ...d, referral_code: res.data.referral_code, referral_url: `https://runrora.com/join?ref=${res.data.referral_code}` }));
    } catch (err) {
      console.error('Error generating code:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: '#8b9cb3' }}>Loading dashboard...</p>
      </div>
    );
  }

  if (!dashboard?.is_pioneer) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF' }}>
        <div style={{ background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)', borderBottom: '1px solid rgba(255, 138, 61, 0.2)', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', position: 'sticky', top: 0, zIndex: 100 }}>
          <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#FF8A3D', fontSize: '1.5rem', cursor: 'pointer' }}>←</button>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Referral Dashboard</h1>
        </div>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
          <p style={{ color: '#8b9cb3', fontSize: '1.1rem' }}>The Pioneer program is exclusive to the first 100 Runrora members.</p>
        </div>
      </div>
    );
  }

  const progress = Math.min(dashboard.active_referrals, 10);

  return (
    <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)', borderBottom: '1px solid rgba(255, 138, 61, 0.2)', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#FF8A3D', fontSize: '1.5rem', cursor: 'pointer' }}>←</button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Referral Dashboard</h1>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem' }}>
        {/* Pioneer Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 138, 61, 0.1) 0%, rgba(255, 138, 61, 0.05) 100%)',
          border: '1px solid rgba(255, 138, 61, 0.3)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
            <PioneerBadge isPioneer={true} pioneerNumber={dashboard.pioneer_number} isCommunityBuilder={dashboard.is_community_builder} />
          </div>
          <p style={{ color: '#8b9cb3', fontSize: '0.9rem', margin: '0.5rem 0 0' }}>
            You are one of the first 100 founding members of Runrora
          </p>
        </div>

        {/* Referral Link */}
        <div style={{
          background: '#16161D',
          border: '1px solid rgba(255, 138, 61, 0.1)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
        }}>
          <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 1rem' }}>
            Your Referral Link
          </h3>
          {dashboard.referral_code ? (
            <>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem',
                fontSize: '0.85rem',
                color: '#FF8A3D',
                wordBreak: 'break-all',
              }}>
                {dashboard.referral_url}
              </div>
              <button
                onClick={handleCopyLink}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: copied ? 'rgba(74, 222, 128, 0.15)' : '#FF8A3D',
                  color: copied ? '#4ade80' : '#0B0B10',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </>
          ) : (
            <button
              onClick={handleGenerateCode}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#FF8A3D',
                color: '#0B0B10',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Generate Referral Code
            </button>
          )}
        </div>

        {/* Progress */}
        <div style={{
          background: '#16161D',
          border: '1px solid rgba(255, 138, 61, 0.1)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
        }}>
          <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.75rem' }}>
            Referral Progress
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#FF8A3D' }}>{dashboard.active_referrals}/10</span>
            <span style={{ fontSize: '0.85rem', color: '#8b9cb3', alignSelf: 'flex-end' }}>active referrals</span>
          </div>
          {/* Progress bar */}
          <div style={{
            height: '8px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '1rem',
          }}>
            <div style={{
              height: '100%',
              width: `${(progress / 10) * 100}%`,
              background: progress >= 10 ? '#4ade80' : '#FF8A3D',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }} />
          </div>
          {dashboard.is_community_builder ? (
            <div style={{
              background: 'rgba(167, 139, 250, 0.1)',
              border: '1px solid rgba(167, 139, 250, 0.3)',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
              fontSize: '0.85rem',
              color: '#a78bfa',
            }}>
              You've unlocked Community Builder status! You're eligible for Runrora gear when our merch launches.
            </div>
          ) : (
            <p style={{ color: '#8b9cb3', fontSize: '0.85rem', margin: 0 }}>
              Invite runners and help them stay active (2+ logins/week). At 10 active referrals, unlock Community Builder status and free Runrora gear.
            </p>
          )}
          <div style={{ fontSize: '0.8rem', color: '#8b9cb3', marginTop: '0.75rem' }}>
            Total referred: {dashboard.total_referrals}
          </div>
        </div>

        {/* Referral List */}
        {dashboard.referrals.length > 0 && (
          <div style={{
            background: '#16161D',
            border: '1px solid rgba(255, 138, 61, 0.1)',
            borderRadius: '16px',
            padding: '1.5rem',
          }}>
            <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 1rem' }}>
              Your Referrals
            </h3>
            {dashboard.referrals.map(ref => {
              const name = ref.first_name
                ? `${ref.first_name} ${ref.last_name || ''}`.trim()
                : ref.name || 'Runner';
              return (
                <div key={ref.user_id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem 0',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: ref.profile_photo ? 'transparent' : 'rgba(255, 138, 61, 0.2)',
                      color: '#FF8A3D',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      flexShrink: 0,
                    }}>
                      {ref.profile_photo ? (
                        <img src={ref.profile_photo} alt={`${ref.name || 'Referral'} avatar`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        name.substring(0, 2).toUpperCase()
                      )}
                    </div>
                    <span style={{ color: '#FFFFFF', fontWeight: 500, fontSize: '0.9rem' }}>{name}</span>
                  </div>
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.2rem 0.6rem',
                    borderRadius: '10px',
                    background: ref.is_active ? 'rgba(74, 222, 128, 0.15)' : 'rgba(139, 156, 179, 0.1)',
                    color: ref.is_active ? '#4ade80' : '#8b9cb3',
                  }}>
                    {ref.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralDashboard;
